import { type PropsWithChildren, useMemo } from 'react';

import { PageSkeleton } from '@kc-console/common-ui';
import { useFindUrlMatchedRoute, useServiceMetaQuery } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { ErrorCardTemplate } from '@kc-console/shared-ui';

import { getPageSkeletonVariant } from '../../../utils/skeleton';
import { InServiceErrorLayout } from '../../ErrorLayout/InServiceErrorLayout';
import ExternalLinkButton from '../../InServiceError/components/ExternalLinkButton';

import type { PageSkeletonVariant } from '@kc-console/common-ui';
import type { ErrorCardTemplateProps } from '@kc-console/shared-ui';

export type UsefulInfo = {
  description?: string;
  linkInfos?: {
    guideKey: string;
    title: string;
  }[];
  title?: string;
};

export interface ExceptionBoundaryProps
  extends Pick<ErrorCardTemplateProps, 'title' | 'description' | 'illustration' | 'actions'> {
  isLoading: boolean;
  isValid: boolean;
  skeletonVariant?: PageSkeletonVariant;
  usefulInfo?: UsefulInfo;
}

/**
 * 서비스 활성화 상태에 따라 예외 화면을 표시하는 컴포넌트
 * @description
 * - isLoading 상태일 때 PageSkeleton을 표시합니다.
 * - isValid가 false일 때 actions가 포함된 안내 화면을 표시합니다.
 * - PageStartBoundary, PageAgentBoundary의 기반 컴포넌트입니다.
 * @example
 * <ExceptionBoundary
 *   title="서비스 시작하기"
 *   description="서비스를 이용하려면 아래 버튼을 클릭해 주세요."
 *   illustration={<IllustStatusStart />}
 *   isLoading={isLoading}
 *   isValid={isActivated}
 * >
 *   <ServiceContent />
 * </ExceptionBoundary>
 */
export default function ExceptionBoundary({
  skeletonVariant = 'list',
  isValid,
  isLoading,
  usefulInfo,
  children,
  ...props
}: PropsWithChildren<ExceptionBoundaryProps>) {
  const { t } = useTranslation();
  const urlMatchedRoute = useFindUrlMatchedRoute();
  const { data: serviceMeta } = useServiceMetaQuery();

  const { guides = {} } = serviceMeta || {};

  const pageSkeletonVariant = getPageSkeletonVariant(urlMatchedRoute?.skeletonType);

  const usefulInfoProps = useMemo(() => {
    if (usefulInfo) {
      const {
        title = t('layout:exception_page.useful_info.title'),
        description = '',
        linkInfos = [],
      } = usefulInfo;
      const actions = linkInfos.map(linkInfo => {
        const { guideKey, title: linkTitle } = linkInfo;
        return (
          <ExternalLinkButton key={guideKey} title={linkTitle} url={guides[guideKey] || guideKey} />
        );
      });
      return {
        title,
        description,
        actions: <>{actions}</>,
      };
    }
    return;
  }, [usefulInfo, guides, t]);

  if (isLoading) {
    return <PageSkeleton variant={pageSkeletonVariant || skeletonVariant} />;
  }

  if (!isValid) {
    return (
      <InServiceErrorLayout usefulInfoProps={usefulInfoProps}>
        <ErrorCardTemplate {...props} />
      </InServiceErrorLayout>
    );
  }

  return children;
}
